import { useIsFetching } from '@tanstack/lit-query';
import { queryClient } from 'Api/query-client';
import { slidesQueryOptions } from 'Api/videos';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Video slides button.
 *
 * Triggers an event to initiate slides API request and/or slides-viewer toggle open.
 *
 * @attr video-slug - Slug used to look up slides GET request status as loading state flag.
 *
 * @fires vm-video-slides:request-open - Bubbles + composed + cancelable.
 */
@customElement('vm-video-slides-button')
export class VmVideoSlidesButton extends LitElement {
    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    // plug into tanstack query for fetching state
    private _fetching = useIsFetching(
        this,
        () => ({ queryKey: slidesQueryOptions(this.videoSlug).queryKey }),
        queryClient,
    );

    private _onClick = (): void => {
        this.dispatchEvent(
            new CustomEvent('vm-video-slides:request-open', {
                bubbles: true,
                composed: true,
                cancelable: true,
            }),
        );
    };

    override render() {
        const isLoading = this._fetching.current > 0;

        return html`
            <vm-button ?loading=${isLoading} variant="solid" color-scheme="primary" size="sm" @click=${this._onClick}>
                <vm-icon slot="icon-leading" name="slides" size="sm"></vm-icon>
                View slides
            </vm-button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-slides-button': VmVideoSlidesButton;
    }
}
